import {
    get,
    overSome,
    compose,
    getOr,
    sortBy,
    find,
    minBy,
    values,
    isEqual,
    isEmpty,
    reject,
    pick,
    isNil,
    map,
    __,
    reduce,
} from 'lodash/fp';
import { createSelector } from 'reselect';
import { getProp, getStringOrNullProp, getStringProp } from '@mews/fp';
import { FeatureFlagService, createAsyncMetaSelectors, createScopedSelector } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { ApplicationState } from '@mews-commander/types';
import { SellLimitStatistic } from '@mews-commander/data-access';
import { createEntityToEntityMapper } from '@mews-commander/utils';
import { getRateGroups } from '@mews-commander/legacy-entities/src/ducks/rateGroups';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import { getServiceEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/services';
import { canScopeOverrideRestrictions } from '@mews-commander/legacy-entities/src/selectors/session/privileges';
import { moduleSelector } from '../module';
import { getExistingRateId, localizeWithoutSymbol } from '../utils/func';
import { getPricingKey, getReservationKey } from '../utils/keys';
import { CLIENT_ID } from '../constants';
import { ReservationOrder } from '../types';
import {
    isSelectedServiceAvailabilityFetching,
    hasSelectedServiceAvailabilityFailed,
    getSelectedServiceCategoryAvailability,
} from './availability';
import { getPartnerCompanyId, getTravelAgencyId } from './options';
import { getServiceId } from './screens';
import {
    getOrder,
    getStartDate,
    getEndDate,
    getPurpose,
    getBusinessSegmentId,
    getVoucherId,
    getProductIds,
    getOccupancies,
    getCategoryOccupancies,
    getStartTime,
    getEndTime,
} from './order';
import { areRateGroupsEmpty, getStoredAvailableRateGroupIds, getMatrixRateGroups } from './rateGroups';
import { getSelectedService, getSelectedServiceUserProfileId } from './entities';
import { getServiceStartDateUtc, getServiceEndDateUtc, getDateTimeUtcString } from './dateConversions';
import { getReservationValues } from './reservations';

const getPricingScopes = createSelector(moduleSelector, get('pricing'));
const getPricing = createScopedSelector({
    parentSelector: getPricingScopes,
    initialState: { ...META_STATE_SUCCEEDED },
});

const getPricingData = createSelector(getPricing, get('data'));
const getPricingMeta = createSelector(getPricing, get('meta'));

const getPricingPendingRequestIds = createSelector(getPricing, get('pendingRequestIds'));
const hasAnyPendingRequestIds = createSelector(
    getPricingPendingRequestIds,
    (ids) => ids !== undefined && !isEmpty(ids)
);

export const [isAnyOccupancyPricingFetching, hasAnyOccupancyPricingFailed] = createAsyncMetaSelectors(getPricingMeta);

export const getRateIdFromProps = getStringProp('rateId');

const getBasePricingOptions = createSelector(
    getStartDate,
    getEndDate,
    getStartTime,
    getEndTime,
    getPurpose,
    getBusinessSegmentId,
    getVoucherId,
    getPartnerCompanyId,
    getTravelAgencyId,
    getProductIds,
    (
        startDate,
        endDate,
        startTime,
        endTime,
        purpose,
        businessSegmentId,
        voucherId,
        partnerCompanyId,
        travelAgencyId,
        productIds
    ) => ({
        startDate,
        endDate,
        startTime,
        endTime,
        purpose,
        businessSegmentId,
        voucherId,
        partnerCompanyId,
        travelAgencyId,
        productIds,
    })
);

export const getPricingOptions = createSelector(
    getBasePricingOptions,
    getServiceId,
    getServiceStartDateUtc,
    getServiceEndDateUtc,
    getCategoryOccupancies,
    (basePricingOptions, serviceId, startUtc, endUtc, categoryOccupancies) => ({
        ...basePricingOptions,
        serviceId,
        startUtc,
        endUtc,
        categoryOccupancies,
    })
);

export const isAnyOccupancyPricingFetchingOrRateGroupsAreEmpty = overSome([
    hasAnyPendingRequestIds,
    isAnyOccupancyPricingFetching,
    areRateGroupsEmpty,
]);

export const isAnyOccupancyPricingOrAvailabilityFetching = overSome([
    isAnyOccupancyPricingFetching,
    isSelectedServiceAvailabilityFetching,
]);

export const hasAnyOccupancyPricingOrAvailabilityFailed = overSome([
    hasAnyOccupancyPricingFailed,
    hasSelectedServiceAvailabilityFailed,
]);

const getAvailableRateGroups = createEntityToEntityMapper(getRateGroups, getStoredAvailableRateGroupIds);

export const getOrderedAvailableRateGroupIds = compose(
    map('id'),
    sortBy('ordering'),
    sortBy('name'),
    getAvailableRateGroups
);

export const getRateGroupRateIds = createSelector(
    getStringProp('rateGroupId'),
    getMatrixRateGroups,
    (rateGroupId, matrixRateGroups) => get(rateGroupId, matrixRateGroups)
);

const defaultToAveragePerNight = ({ averagePerNightBeforeConversion, averagePerNight }: $TSFixMe) =>
    isNil(averagePerNightBeforeConversion) ? averagePerNight.value : averagePerNightBeforeConversion.value;

export const getResourceCategoryTypeCategoryPricing = createSelector(
    getStringProp('resourceCategoryTypeCategoryId'),
    getBasePricingOptions,
    getOccupancies,
    getPricingData,
    (resourceCategoryTypeCategoryId, baseOptions, occupancies, pricing) =>
        compose(
            pick(['rateGroups', 'rates']),
            get(__, pricing),
            ({ occupancyData, categoryId }) => getPricingKey({ ...baseOptions, categoryId, occupancyData }),
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            find({ categoryId: resourceCategoryTypeCategoryId })
            // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
        )(occupancies)
);

const getPricingRates = createSelector(
    getStringProp('categoryId'),
    getBasePricingOptions,
    getOccupancies,
    getPricingData,
    (categoryId, baseOptions, occupancies = {}, pricing) =>
        compose(
            getOr({}, 'rates'),
            get(__, pricing),
            (occupancy = {}) =>
                getPricingKey({
                    ...baseOptions,
                    categoryId,
                    occupancyData: (occupancy as $TSFixMe).occupancyData,
                }),
            find({ categoryId })
        )(occupancies)
);

const getPricingRate = createSelector(getRateIdFromProps, getPricingRates, (rateId, pricingRates) =>
    get(getExistingRateId(rateId), pricingRates)
);

const getRateOffer = createSelector(getPricingRate, get('offer'));
export const getRateOfferPrices = createSelector(
    getRateOffer,
    pick([
        'averagePerNight',
        'averagePerNightBeforeConversion',
        'averagePerFullTimeUnit',
        'averageServiceItemCostPerNight',
        'averageServiceItemCostPerFullTimeUnit',
        'total',
    ])
);
export const getRateOfferVoucherId = createSelector(getRateOffer, get('voucherId'));

export const isRateRestricted = createSelector(
    getProp<FeatureFlagService>()('featureFlagService'),
    (state: ApplicationState) => state,
    getRateOffer,
    getSelectedServiceUserProfileId,
    (featureFlagService, state: ApplicationState, rateOffer, selectedServiceProfileId: string) => {
        const canOverrideRestrictions = canScopeOverrideRestrictions(state, { profileId: selectedServiceProfileId });

        if (featureFlagService.hasFlag(FeatureFlag.overrideRestrictionsEnabled) && canOverrideRestrictions) {
            return false;
        }

        return rateOffer?.isRestricted;
    }
);

/**
 * This should be called only when FeatureFlag.sellLimitsEnabled is enabled
 */
export const isRateSellLimited = createSelector(
    getStringProp('rateId'),
    getStringProp('categoryId'),
    getStartDate,
    getEndDate,
    getSelectedServiceCategoryAvailability,
    getRateOffer,
    getReservationValues,
    (rateId, categoryId, startDate, endDate, selectedAvailability, offer, reservations) => {
        if (offer?.sellLimitStatistic === null || offer?.sellLimitStatistic === undefined) {
            return false;
        }

        const { available } = offer.sellLimitStatistic as SellLimitStatistic;

        if (available === undefined) {
            return false;
        }

        const basketReservationCount = reduce(
            (
                acc: number,
                r: { rateId: string; categoryId: string; count: number; startDate: string; endDate: string }
            ) =>
                acc +
                (r.rateId === rateId &&
                r.categoryId === categoryId &&
                r.startDate === startDate &&
                r.endDate === endDate
                    ? r.count
                    : 0),
            0
        )(reservations);

        return basketReservationCount + selectedAvailability.selectedCount > available;
    }
);

export const getLocalizedRateOfferPrices = createSelector(
    getRateOfferPrices,
    isRateRestricted,
    ({ averagePerFullTimeUnit }, isRestricted) =>
        !isRestricted && {
            averagePerFullTimeUnitValue: !isNil(averagePerFullTimeUnit)
                ? localizeWithoutSymbol(averagePerFullTimeUnit)
                : null,
        }
);

const getCheapestPricingRate = createSelector(
    getPricingRates,
    compose(
        minBy(compose(defaultToAveragePerNight, pick(['averagePerNightBeforeConversion', 'averagePerNight']))),
        reject('isRestricted'),
        map(({ offer, rateId }) => ({ ...offer, rateId })),
        values
    )
);

export const isCheapestPricingRate = createSelector(
    getRateIdFromProps,
    getCheapestPricingRate,
    (rateId, cheapestPricingRate) => isEqual(getOr('', 'rateId', cheapestPricingRate), getExistingRateId(rateId))
);

const getReservationCustomPrice = createSelector(
    getProp()('customPriceTotal'),
    getProp()('customRatePerFullTimeUnit'),
    (customPriceTotal, customRatePerFullTimeUnit) => ({
        customTotal: customPriceTotal,
        customTotalBeforeConversion: customPriceTotal,
        customNightCost: customRatePerFullTimeUnit,
    })
);

export const getReservationFromOrder = createSelector(
    getOrder,
    getServiceStartDateUtc,
    getServiceEndDateUtc,
    getRateIdFromProps,
    getStringProp('categoryId'),
    getReservationCustomPrice,
    getProp()('customNightCostToShow'),
    getStringOrNullProp('businessSegmentId'),
    (
        { occupancies, ...baseOrder },
        startUtc,
        endUtc,
        rateId,
        categoryId,
        reservationCustomPrice,
        customNightCostToShow,
        businessSegmentId
    ): ReservationOrder => {
        const { customNightCost } = reservationCustomPrice;
        const occupancy = find(['categoryId', categoryId])(occupancies);

        const identifier = getReservationKey({
            ...baseOrder,
            ...occupancy,
            rateId,
            businessSegmentId,
            customNightCost: reservationCustomPrice,
        });

        return {
            ...baseOrder,
            ...occupancy,
            identifier,
            categoryId,
            rateId,
            businessSegmentId,
            customNightCost,
            customNightCostToShow,
            startUtc,
            endUtc,
        };
    }
);

const getBaseReservationProperties = createSelector(
    getReservationFromOrder,
    pick([
        'adultCount',
        'childCount',
        'occupancyData',
        'productIds',
        'rateId',
        'businessSegmentId',
        'categoryId',
        'startDate',
        'endDate',
        'startTime',
        'endTime',
        'purpose',
    ])
);

export const getReservationWithCustomPrice = createSelector(
    getSelectedService,
    getServiceEnterpriseIanaTimeZoneIdentifier,
    getBaseReservationProperties,
    getProp()('customNightCost'),
    (service, ianaTimeZoneIdentifier, baseProperties, customNightCost) => {
        const { arrivalTime, departureTime, id: serviceId } = service;
        const {
            categoryId,
            startDate,
            endDate,
            startTime,
            endTime,
            adultCount,
            childCount,
            occupancyData,
            productIds,
            ...other
        } = baseProperties;
        return {
            ...other,
            productOrders: productIds.map((productId: string) => ({ productId })),
            occupancyData: values(occupancyData),
            clientId: CLIENT_ID,
            serviceId,
            startUtc: getDateTimeUtcString(startDate, startTime || arrivalTime, ianaTimeZoneIdentifier),
            endUtc: getDateTimeUtcString(endDate, endTime || departureTime, ianaTimeZoneIdentifier),
            requestedCategoryId: categoryId,
            customNightCost,
        };
    }
);
